export const REGIONS = [
  { id: "maghreb", color: "#C8553D", codes: ["MR", "MA", "EH", "DZ", "TN", "LY"] },
  { id: "nile", color: "#2F6F5E", codes: ["EG", "SD"] },
  { id: "mashreq", color: "#B8860B", codes: ["PS", "JO", "LB", "SY", "IQ"] },
  { id: "gulf", color: "#3B6FA0", codes: ["SA", "YE", "OM", "AE", "QA", "BH", "KW"] },
  { id: "horn", color: "#8C5A2B", codes: ["SO", "DJ", "KM"] },
];

export const REGION_OF = Object.fromEntries(REGIONS.flatMap((r) => r.codes.map((c) => [c, r])));

export const INTRO = {
  it: {
    kicker: "Introduzione",
    title: "Un mondo, cinque grandi aree",
    lead: "L'Atlante raggruppa i 22 Stati della Lega Araba, la Palestina e il Sahara Occidentale in cinque macro-aree geografiche, dall'Atlantico al Golfo e dal Mediterraneo all'Oceano Indiano. A unirli sono la lingua araba e una storia comune; a distinguerli, dialetti, paesaggi, religioni e risorse.",
    map_hint: "Tocca un'area o un paese sulla mappa",
    regions: {
      maghreb: { name: "Il Maghreb", sub: "Nord Africa occidentale" },
      nile: { name: "La Valle del Nilo", sub: "Nord-est africano" },
      mashreq: { name: "Il Mashreq", sub: "Vicino Oriente" },
      gulf: { name: "Penisola Arabica e Golfo", sub: "Asia sud-occidentale" },
      horn: { name: "Corno d'Africa e Oceano Indiano", sub: "Africa orientale" },
    },
    keys_title: "Elementi chiave",
    keys: [
      { h: "Omogeneità linguistica e culturale", p: "L'arabo è la lingua ufficiale e unificante, ma i dialetti parlati — marocchino, egiziano, levantino, del Golfo — differiscono profondamente tra loro." },
      { h: "Dimensione religiosa", p: "Il mondo arabo è in larga maggioranza musulmano (sunniti e sciiti) ma ospita antiche e importanti minoranze cristiane ed ebraiche. Non va confuso con il mondo islamico: Turchia, Iran e Indonesia sono musulmane ma non arabe." },
      { h: "Risorse naturali e confini", p: "Il Golfo concentra enormi riserve di petrolio e gas naturale; altrove la sfida è la gestione dell'acqua, scarsa in territori dominati dal Sahara e dal deserto Arabico." },
    ],
  },
  en: {
    kicker: "Introduction",
    title: "One world, five great regions",
    lead: "The Atlas groups the 22 Arab League states, Palestine and Western Sahara into five geographic macro-regions, from the Atlantic to the Gulf and from the Mediterranean to the Indian Ocean. The Arabic language and a shared history unite them; dialects, landscapes, religions and resources set them apart.",
    map_hint: "Tap a region or a country on the map",
    regions: {
      maghreb: { name: "The Maghreb", sub: "Western North Africa" },
      nile: { name: "The Nile Valley", sub: "North-east Africa" },
      mashreq: { name: "The Mashreq", sub: "Near East" },
      gulf: { name: "Arabian Peninsula and Gulf", sub: "South-west Asia" },
      horn: { name: "Horn of Africa and Indian Ocean", sub: "East Africa" },
    },
    keys_title: "Key elements",
    keys: [
      { h: "Linguistic and cultural unity", p: "Arabic is the official, unifying language, yet the spoken dialects — Moroccan, Egyptian, Levantine, Gulf — differ deeply from one another." },
      { h: "Religion", p: "The Arab world is overwhelmingly Muslim (Sunni and Shia) but is home to ancient and significant Christian and Jewish minorities. It is not the Islamic world: Turkey, Iran and Indonesia are Muslim but not Arab." },
      { h: "Natural resources and borders", p: "The Gulf concentrates huge oil and natural-gas reserves; elsewhere the challenge is water, scarce in lands dominated by the Sahara and the Arabian Desert." },
    ],
  },
  fr: {
    kicker: "Introduction",
    title: "Un monde, cinq grandes régions",
    lead: "L'Atlas regroupe les 22 États de la Ligue arabe, la Palestine et le Sahara occidental en cinq macro-régions géographiques, de l'Atlantique au Golfe et de la Méditerranée à l'océan Indien. La langue arabe et une histoire commune les unissent ; dialectes, paysages, religions et ressources les distinguent.",
    map_hint: "Touchez une région ou un pays sur la carte",
    regions: {
      maghreb: { name: "Le Maghreb", sub: "Afrique du Nord occidentale" },
      nile: { name: "La vallée du Nil", sub: "Nord-Est africain" },
      mashreq: { name: "Le Machrek", sub: "Proche-Orient" },
      gulf: { name: "Péninsule arabique et Golfe", sub: "Asie du Sud-Ouest" },
      horn: { name: "Corne de l'Afrique et océan Indien", sub: "Afrique de l'Est" },
    },
    keys_title: "Éléments clés",
    keys: [
      { h: "Unité linguistique et culturelle", p: "L'arabe est la langue officielle et unificatrice, mais les dialectes parlés — marocain, égyptien, levantin, du Golfe — diffèrent profondément." },
      { h: "Dimension religieuse", p: "Le monde arabe est très majoritairement musulman (sunnites et chiites) mais abrite d'anciennes et importantes minorités chrétiennes et juives. Il ne se confond pas avec le monde islamique : la Turquie, l'Iran et l'Indonésie sont musulmans mais pas arabes." },
      { h: "Ressources naturelles et frontières", p: "Le Golfe concentre d'immenses réserves de pétrole et de gaz ; ailleurs, le défi est l'eau, rare dans des territoires dominés par le Sahara et le désert d'Arabie." },
    ],
  },
  ar: {
    kicker: "مقدمة",
    title: "عالم واحد، خمس مناطق كبرى",
    lead: "يجمع الأطلس دول جامعة الدول العربية الاثنتين والعشرين وفلسطين والصحراء الغربية في خمس مناطق جغرافية كبرى، من المحيط الأطلسي إلى الخليج ومن البحر المتوسط إلى المحيط الهندي. توحّدها اللغة العربية والتاريخ المشترك، وتميّزها اللهجات والمناظر الطبيعية والأديان والموارد.",
    map_hint: "المس منطقة أو دولة على الخريطة",
    regions: {
      maghreb: { name: "المغرب العربي", sub: "غرب شمال أفريقيا" },
      nile: { name: "وادي النيل", sub: "شمال شرق أفريقيا" },
      mashreq: { name: "المشرق العربي", sub: "الشرق الأدنى" },
      gulf: { name: "شبه الجزيرة العربية والخليج", sub: "جنوب غرب آسيا" },
      horn: { name: "القرن الأفريقي والمحيط الهندي", sub: "شرق أفريقيا" },
    },
    keys_title: "عناصر أساسية",
    keys: [
      { h: "وحدة لغوية وثقافية", p: "العربية هي اللغة الرسمية الجامعة، غير أن اللهجات المحكية — المغربية والمصرية والشامية والخليجية — تختلف اختلافاً عميقاً فيما بينها." },
      { h: "البعد الديني", p: "العالم العربي مسلم في غالبيته الساحقة (سنّة وشيعة) لكنه يضم أقليات مسيحية ويهودية عريقة ومهمة. ولا ينبغي الخلط بينه وبين العالم الإسلامي: فتركيا وإيران وإندونيسيا دول مسلمة لكنها ليست عربية." },
      { h: "الموارد الطبيعية والحدود", p: "يتركّز في الخليج احتياطي هائل من النفط والغاز الطبيعي؛ وفي أماكن أخرى يكمن التحدي في إدارة المياه الشحيحة في أراضٍ تهيمن عليها الصحراء الكبرى والصحراء العربية." },
    ],
  },
};
